/**
 * The centred field a blank website card shows on the preview stage.
 *
 * `+ website` used to stop on a modal and ask for the URL before it would make anything, so the
 * button opened a box rather than adding a website. It mints a blank card now (`useDeckCreate`,
 * `status: 'empty'`) and the question is asked HERE, on the stage the card already owns — the row is
 * in the directory, selected, and the pane is showing you the thing you just made with the one field
 * it is still missing.
 *
 * ## Copied from the canvas, and copied deliberately
 *
 * The interaction is `BirtvyWebCard`'s `EmptyWebCardBody` — the blank card the canvas has drawn since
 * `addEmptyWeb`: ONE pill with the border on the pill and a BARE input inside it (nesting the design
 * system's `Input` would draw a second box inside the first), a `global` glyph leading, autofocus,
 * Enter submits, and a submit button inside the pill that is the only thing that changes as you type
 * — muted while there is nothing to send, filled the moment there is. Two surfaces asking the same
 * question must ask it the same way.
 *
 * The IMPLEMENTATION is not shared, and could not be. That component is Tailwind on the canvas's
 * `--color-*` tokens and sizes itself in container-query units against a world-space card; this is a
 * v1.5 part on the `--hv15-*` ramp at fixed pane pixels. Importing it would drag the canvas's palette
 * onto the deck's chrome and scale the control to whatever the stage happens to be that render.
 *
 * ## What it is not
 *
 * Not a `StageMessage`. That block is a statement — a dimmed glyph, a title, a sentence and an
 * optional way out — and it is what the pane draws when there is nothing open. This is the opposite
 * case: something IS open, and it is asking you a question. The one line under the pill says what
 * pressing return will do, not what has gone wrong.
 *
 * Presentational and stateless apart from the text being typed. `onSubmit` is the caller's; see
 * `artifactStage`, which hands it the resolve seam.
 */
import { useState } from 'react';
import { cn } from '~/lib/utils';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import type { HeaderTheme } from '~/design-system/components/v1_5/Preview';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';
import styles from './WebsiteIntake.module.css';

export interface WebsiteIntakeProps {
  /**
   * The address, as typed. Never trimmed or canonicalised here — `toWebHref` is the one place that
   * decides what `usedeck.ai` means, and a second opinion in a text field is how two of them appear.
   */
  onSubmit: (url: string) => void;
  /**
   * The page is being read.
   *
   * It exists because the fetch now happens BEFORE anything is written (`useDeckCreate.createWebsite`):
   * there is no directory row yet to carry `updating`, so the wait has to be shown on the only thing
   * on screen that caused it. The field locks rather than unmounting — the address stays readable, and
   * a scrape that fails leaves the user looking at what they typed instead of an empty pill.
   */
  pending?: boolean;
  theme?: HeaderTheme;
  className?: string;
}

export function WebsiteIntake({ onSubmit, pending = false, theme: themeProp, className }: WebsiteIntakeProps) {
  const theme = useV15Theme(themeProp);
  const [value, setValue] = useState('');
  const ready = value.trim().length > 0 && !pending;
  const submit = () => {
    if (ready) onSubmit(value);
  };

  return (
    <div className={cn(themes[theme], styles.intake, className)}>
      {/* `aria-busy` and not a spinner: the registry has no loader glyph, and the honest reading of
          this wait is that the CONTROL is working, not that a new object has appeared to spin. */}
      <div className={cn(styles.pill, pending && styles.pending)} aria-busy={pending || undefined}>
        <DeckIcon className={styles.glyph} name="global" size={18} aria-hidden />
        <input
          className={styles.input}
          // The card was made by pressing a button that says "website"; the caret belongs in the one
          // field that finishes the job. Nothing else on this stage can take focus.
          autoFocus
          value={value}
          readOnly={pending}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            // A bare input inside a form-less pill still fires the page's default on Enter in some
            // engines; the submit is ours either way.
            event.preventDefault();
            submit();
          }}
          placeholder="Paste a link"
          aria-label="Website URL"
          // `inputMode`, deliberately NOT `type="url"`. The type brings constraint validation with
          // it, and `usedeck.ai` — which `toWebHref` resolves perfectly well — is not a valid URL by
          // that rule, so the field would paint itself invalid for an address it accepts. The
          // inputmode is the half that was actually wanted: the right keyboard on a phone.
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          className={styles.submit}
          type="button"
          onClick={submit}
          // Disabled rather than hidden: the button is where the eye expects the action to be from
          // the first frame, and a control that appears as you type moves the pill's contents under
          // the pointer.
          disabled={!ready}
          aria-label="Add website"
        >
          <DeckIcon name="arrow-up" size={18} aria-hidden />
        </button>
      </div>
    </div>
  );
}
